import { useEffect, useLayoutEffect, useId, useRef, useState, type CSSProperties, type ReactNode } from "react";
import "./canvas-tools.css";
import type { CanvasEditor } from "../controller/types";
import type { CanvasMode, CanvasModeConfiguration, CanvasTool } from "../domain/types";
import { applyCanvasModeBehavior, switchCanvasMode } from "../modes";
import { useCanvasMessages } from "./i18n";

const TOOL_SHORT_LABELS: Readonly<Record<CanvasTool, string>> = {
  select: "↖", lasso: "⌁", hand: "✋", sticky: "▤", text: "T",
  rectangle: "□", ellipse: "○", diamond: "◇", arrow: "→", draw: "✎", eraser: "▱",
  image: "▧", frame: "⌗", reference: "↗",
};

const TOOL_SHORTCUTS: Partial<Record<CanvasTool, string>> = {
  select: "V", lasso: "L", hand: "H", sticky: "N", text: "T",
  rectangle: "R", ellipse: "O", arrow: "A", draw: "D", eraser: "E", frame: "F",
};

const toolbarStyle: CSSProperties = {
  position: "absolute", zIndex: 20, top: 12, left: 12, right: 12,
  display: "flex", flexWrap: "wrap", alignItems: "center", gap: 5, padding: 6,
  border: "1px solid #dbe3ee", borderRadius: 12,
  background: "rgba(255,255,255,.96)",
  boxShadow: "0 6px 24px rgba(15,23,42,.12)",
};

const buttonStyle: CSSProperties = {
  width: 40, height: 40, flex: "0 0 40px", border: 0, borderRadius: 8,
  color: "#334155", background: "transparent", fontSize: 18, cursor: "pointer",
};

const menuStyle: CSSProperties = {
  position: "absolute", zIndex: 3, top: 46, display: "grid",
  gridTemplateColumns: "repeat(2, minmax(0, 1fr))", width: 248, gap: 4, padding: 6,
  border: "1px solid #dbe3ee", borderRadius: 10, background: "#fff",
  boxShadow: "0 10px 28px rgba(15,23,42,.16)",
};

export interface CanvasToolbarProps {
  editor: CanvasEditor;
  tool: CanvasTool;
  onToolChange(tool: CanvasTool): void;
  mode: CanvasModeConfiguration;
  outlineVisible?: boolean;
  onToggleOutline?(): void;
  minimapVisible?: boolean;
  onToggleMinimap?(): void;
  layersVisible?: boolean;
  onToggleLayers?(): void;
  disabledTools?: ReadonlySet<CanvasTool>;
  disabledToolReasons?: Partial<Record<CanvasTool, string>>;
  onCreateDiagram?(): void;
  onDuplicate?(): void;
  onFitToScreen?(): void;
  onFitToSelection?(): void;
  onModeChange?(mode: CanvasMode): void;
}

export function CanvasToolbar(props: CanvasToolbarProps) {
  const { editor, tool, onToolChange, mode, outlineVisible, onToggleOutline,
    minimapVisible, onToggleMinimap, layersVisible, onToggleLayers,
    disabledTools, disabledToolReasons, onCreateDiagram, onDuplicate, onFitToScreen, onFitToSelection, onModeChange } = props;
  const [moreOpen, setMoreOpen] = useState(false);
  const moreId = useId();
  const moreTrigger = useRef<HTMLButtonElement>(null);
  const messages = useCanvasMessages();
  const snapshot = editor.getSnapshot();
  const available = (candidate: CanvasTool) =>
    (!["draw", "eraser"].includes(candidate) || mode.features.freehand) &&
    (candidate !== "frame" || mode.features.frames) &&
    (candidate !== "arrow" || mode.features.connections) &&
    (candidate !== "reference" || mode.features.references);
  const disabled = (candidate: CanvasTool) =>
    disabledTools?.has(candidate) === true ||
    (snapshot.readOnly && candidate !== "select" && candidate !== "hand");
  const toolButton = (candidate: CanvasTool) => available(candidate) ? (
    <button
      key={candidate}
      type="button"
      aria-label={messages.tools[candidate]}
      aria-keyshortcuts={TOOL_SHORTCUTS[candidate]}
      aria-description={disabledToolReasons?.[candidate]}
      aria-pressed={tool === candidate}
      title={`${disabledToolReasons?.[candidate] ?? messages.tools[candidate]}${TOOL_SHORTCUTS[candidate] ? ` (${TOOL_SHORTCUTS[candidate]})` : ""}`}
      disabled={disabled(candidate)}
      onClick={() => onToolChange(candidate)}
      style={{ ...buttonStyle, color: tool === candidate ? "#fff" : "#334155", background: tool === candidate ? "#4f46e5" : "transparent", opacity: disabled(candidate) ? 0.38 : 1 }}
    ><span aria-hidden="true">{TOOL_SHORT_LABELS[candidate]}</span><span className="canvas-tool-caption">{messages.tools[candidate]}</span></button>
  ) : null;
  const selectionCount = snapshot.selection.nodeIds.size;
  const editableSelectionIds = snapshot.canvas.nodes
    .filter((node) => snapshot.selection.nodeIds.has(node.id) && node.props.locked !== true)
    .map((node) => node.id);
  const editableSelectionCount = editableSelectionIds.length;

  return (
    <div className="canvas-main-toolbar" role="toolbar" aria-label={messages.toolbarLabel} style={toolbarStyle}>

      <span role="group" aria-label="常用工具" style={{ display: "flex", gap: 2 }}>
        {["select", "hand", "draw", "text", "eraser", "arrow"].map((candidate) => toolButton(candidate as CanvasTool))}
      </span>

      <span aria-hidden="true" style={{ width: 1, height: 28, background: "#dbe3ee", margin: "0 2px" }} />
      <ActionButton label={messages.undo} shortcut="Ctrl+Z" disabled={!snapshot.canUndo || snapshot.readOnly} onClick={() => editor.undo()}>↶</ActionButton>
      <ActionButton label={messages.redo} shortcut="Ctrl+Shift+Z" disabled={!snapshot.canRedo || snapshot.readOnly} onClick={() => editor.redo()}>↷</ActionButton>

      <button type="button" ref={moreTrigger} style={buttonStyle} aria-label="更多画板工具" aria-expanded={moreOpen} aria-controls={moreId} onClick={() => setMoreOpen(!moreOpen)}><span aria-hidden="true">···</span><span className="canvas-tool-caption">更多</span></button>
      <div id={moreId} hidden={!moreOpen} className="canvas-secondary-tools" role="group" aria-label="更多画板工具" onKeyDown={(event) => {
        if (event.key === "Escape" && !event.defaultPrevented) { event.preventDefault(); event.stopPropagation(); setMoreOpen(false); moreTrigger.current?.focus(); }
      }}>
      <select
        aria-label={messages.modePicker}
        value={mode.mode}
        disabled={snapshot.readOnly}
        title={messages.modeDescriptions[mode.mode]}
        onChange={(event) => {
          const next = event.currentTarget.value as CanvasMode;
          if (onModeChange) onModeChange(next); else switchCanvasMode(editor, next);
        }}
        style={{ height: 36, minWidth: 104, border: "1px solid #cbd5e1", borderRadius: 8, color: "#334155", background: "#fff" }}
      >
        {(Object.keys(messages.modes) as CanvasMode[]).map((candidate) => <option key={candidate} value={candidate}>{messages.modes[candidate]}</option>)}
      </select>

      {toolButton("sticky")}
      {onCreateDiagram && <ActionButton label="软件工程图" disabled={snapshot.readOnly} onClick={onCreateDiagram}>▱</ActionButton>}
      <ToolbarMenu label="图形" icon="◇">
        {["rectangle", "ellipse", "diamond"].map((candidate) => toolButton(candidate as CanvasTool))}
      </ToolbarMenu>
      <ToolbarMenu label="更多创作工具" icon="＋">
        {["lasso", "image", "frame", "reference"].map((candidate) => toolButton(candidate as CanvasTool))}
      </ToolbarMenu>

      <ToolbarMenu label="手写纸" icon="▧">
        <label style={{ gridColumn: "1 / -1", padding: 6 }}>纸张背景<select aria-label="纸张背景" disabled={snapshot.readOnly} value={String(snapshot.canvas.settings.paper ?? "dots")} onChange={(event) => editor.dispatch({ type: "update-settings", settings: { paper: event.currentTarget.value } })}><option value="dots">点阵</option><option value="plain">空白</option><option value="ruled">横线</option></select></label>
        <label style={{ gridColumn: "1 / -1", padding: 6 }}><input type="checkbox" aria-label="默认使用手写字体" disabled={snapshot.readOnly} checked={snapshot.canvas.settings.handwriting === true} onChange={(event) => editor.dispatch({ type: "update-settings", settings: { handwriting: event.currentTarget.checked } })} />默认使用手写字体</label>
      </ToolbarMenu>
      <ToolbarMenu label="排列与图层" icon="▥" align="right">
        {onDuplicate ? <ActionButton label={messages.duplicate} shortcut="Ctrl+D" disabled={snapshot.readOnly || selectionCount === 0} onClick={onDuplicate}>⧉</ActionButton> : null}
        <ActionButton label={messages.group} disabled={snapshot.readOnly || editableSelectionCount < 2} onClick={() => editor.dispatch({ type: "group-nodes", nodeIds: editableSelectionIds })}>⊞</ActionButton>
        <ActionButton label={messages.ungroup} disabled={snapshot.readOnly || editableSelectionCount === 0} onClick={() => editor.dispatch({ type: "ungroup-nodes", nodeIds: editableSelectionIds })}>⊟</ActionButton>
        <ActionButton label={messages.bringFront} disabled={snapshot.readOnly || editableSelectionCount === 0} onClick={() => editor.dispatch({ type: "reorder-nodes", nodeIds: editableSelectionIds, operation: "bring-front" })}>↑</ActionButton>
        <ActionButton label={messages.sendBack} disabled={snapshot.readOnly || editableSelectionCount === 0} onClick={() => editor.dispatch({ type: "reorder-nodes", nodeIds: editableSelectionIds, operation: "send-back" })}>↓</ActionButton>
        <ActionButton label={messages.alignHorizontal} shortcut="Alt+H" disabled={snapshot.readOnly || editableSelectionCount < 2} onClick={() => editor.dispatch({ type: "align-nodes", nodeIds: editableSelectionIds, axis: "y" })}>↔</ActionButton>
        <ActionButton label={messages.alignVertical} shortcut="Alt+V" disabled={snapshot.readOnly || editableSelectionCount < 2} onClick={() => editor.dispatch({ type: "align-nodes", nodeIds: editableSelectionIds, axis: "x" })}>↕</ActionButton>
        {([ ["左对齐", "x", "start"], ["右对齐", "x", "end"], ["顶端对齐", "y", "start"], ["底端对齐", "y", "end"] ] as const).map(([label, axis, anchor]) => <ActionButton key={label} label={label} disabled={snapshot.readOnly || editableSelectionCount < 2} onClick={() => editor.dispatch({ type: "align-nodes", nodeIds: editableSelectionIds, axis, anchor })}>{axis === "x" ? "↔" : "↕"}</ActionButton>)}
        {([ ["水平等间距", "x"], ["垂直等间距", "y"] ] as const).map(([label, axis]) => <ActionButton key={label} label={label} disabled={snapshot.readOnly || editableSelectionCount < 3} onClick={() => editor.dispatch({ type: "distribute-nodes", nodeIds: editableSelectionIds, axis })}>⋮</ActionButton>)}
        {mode.mode !== "freeform" ? <ActionButton label={messages.modeAction} disabled={snapshot.readOnly} onClick={() => applyCanvasModeBehavior(editor, { projectColumns: messages.projectColumns })}>M</ActionButton> : null}
      </ToolbarMenu>


      <ToolbarMenu label="视图与导航" icon="⌗" align="right">
        {onFitToScreen ? <ActionButton label={messages.fitToScreen} shortcut="1" disabled={snapshot.canvas.nodes.length === 0} onClick={onFitToScreen}>⌗</ActionButton> : null}
        {onFitToSelection ? <ActionButton label="缩放到所选内容" shortcut="2" disabled={selectionCount === 0} onClick={onFitToSelection}>◎</ActionButton> : null}
        {onToggleMinimap ? <ActionButton label={minimapVisible ? "隐藏小地图" : "显示小地图"} pressed={minimapVisible === true} onClick={onToggleMinimap}>◫</ActionButton> : null}
        {onToggleLayers ? <ActionButton label={layersVisible ? "隐藏图层" : "显示图层"} pressed={layersVisible === true} onClick={onToggleLayers}>▤</ActionButton> : null}
        {onToggleOutline ? <ActionButton label={outlineVisible ? messages.hideOutline : messages.showOutline} pressed={outlineVisible === true} onClick={onToggleOutline}>☷</ActionButton> : null}
      </ToolbarMenu>
      </div>
    </div>
  );
}

function ToolbarMenu({ label, icon, align = "left", children }: { label: string; icon: string; align?: "left" | "right"; children: ReactNode }) {
  const ref = useRef<HTMLDetailsElement>(null);
  const menu = useRef<HTMLDivElement>(null);
  const [open, setOpen] = useState(false);
  useLayoutEffect(() => {
    if (!open || !menu.current || !ref.current?.closest(".floating-toolbar")) return;
    const element = menu.current;
    const place = () => {
      const anchor = ref.current!.querySelector("summary")!.getBoundingClientRect();
      element.style.setProperty("position", "fixed", "important");
      element.style.setProperty("width", `${Math.min(248, window.innerWidth - 24)}px`, "important");
      element.style.setProperty("max-height", `${Math.max(100, window.innerHeight - 24)}px`, "important");
      const bounds = element.getBoundingClientRect();
      element.style.setProperty("left", `${Math.max(12, Math.min(anchor.left, window.innerWidth - bounds.width - 12))}px`, "important");
      element.style.setProperty("right", "auto", "important");
      element.style.setProperty("top", `${Math.max(12, Math.min(anchor.bottom + 8, window.innerHeight - bounds.height - 12))}px`, "important");
    };
    place();
    window.addEventListener("resize", place);
    return () => window.removeEventListener("resize", place);
  }, [open]);
  useEffect(() => {
    const closeOutside = (event: PointerEvent) => {
      if (ref.current && !ref.current.contains(event.target as Node)) ref.current.open = false;
    };
    document.addEventListener("pointerdown", closeOutside);
    return () => document.removeEventListener("pointerdown", closeOutside);
  }, []);
  return (
    <details ref={ref} onToggle={(event) => setOpen(event.currentTarget.open)} className="canvas-toolbar-menu" style={{ position: "relative" }} onKeyDown={(event) => {
      if (event.key === "Escape" && ref.current) { event.stopPropagation(); ref.current.open = false; ref.current.querySelector("summary")?.focus(); }
    }} onClick={(event) => {
      if ((event.target as Element).closest("button:not(:disabled)") && ref.current) ref.current.open = false;
    }}>
      <summary aria-label={label} title={label} style={{ ...buttonStyle, display: "grid", placeItems: "center", listStyle: "none" }}>
        <span aria-hidden="true">{icon}</span><span className="canvas-tool-caption">{{ "更多创作工具": "插入", "排列与图层": "排列", "视图与导航": "视图" }[label] ?? label}</span>
      </summary>
      <div ref={menu} role="group" aria-label={label} style={{ ...menuStyle, ...(align === "right" ? { right: 0 } : { left: 0 }) }}>{children}</div>
    </details>
  );
}

function ActionButton({ label, shortcut, disabled, pressed, onClick, children }: { label: string; shortcut?: string; disabled?: boolean; pressed?: boolean; onClick(): void; children: ReactNode }) {
  return (
    <button className={label === "软件工程图" ? "canvas-engineering-insert" : undefined} type="button" aria-label={label} aria-keyshortcuts={shortcut} aria-pressed={pressed} title={`${label}${shortcut ? ` (${shortcut})` : ""}`} disabled={disabled} onClick={onClick} style={{ ...buttonStyle, background: pressed ? "#e0e7ff" : "transparent", opacity: disabled ? 0.38 : 1 }}>
      <span aria-hidden="true">{children}</span><span className="canvas-tool-caption">{label}</span>
    </button>
  );
}
